/**
 * Client for /api/uploads.
 *
 * Single file = one concern per export. All functions throw an Error with a
 * user-presentable `.message` AND set `.status` (number) + `.code`
 * (string token) so callers can branch on plan-quota (402) without parsing
 * strings.
 *
 * Why bare `fetch` instead of axios: the rest of UploadPanel was already on
 * fetch; keeping one HTTP library here means no new dependency and one fewer
 * thing to mock in future tests.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _token = () =>
  // Prefer the modern key; fall back to the legacy one used by older builds.
  localStorage.getItem('ivisora_auth_token') ||
  localStorage.getItem('user_token') ||
  '';

const _authHeader = () => {
  const t = _token();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

class UploadsApiError extends Error {
  constructor(message, { status, code } = {}) {
    super(message);
    this.name = 'UploadsApiError';
    this.status = status;
    // Short token for callers: 'quota_exceeded' | 'unauthorized' | 'too_large' |
    // 'unsupported' | 'not_configured' | 'network' | 'unknown'
    this.code = code || 'unknown';
  }
}

const _statusToCode = (status) => {
  if (status === 401) return 'unauthorized';
  if (status === 402) return 'quota_exceeded';
  if (status === 413) return 'too_large';
  if (status === 415) return 'unsupported';
  if (status === 503) return 'not_configured';
  return 'unknown';
};

const _handle = async (resp) => {
  if (resp.ok) return resp.json();
  let detail = `Request failed (${resp.status})`;
  try {
    const body = await resp.json();
    if (body && typeof body.detail === 'string') detail = body.detail;
  } catch {
    /* non-JSON error body */
  }
  throw new UploadsApiError(detail, {
    status: resp.status,
    code: _statusToCode(resp.status),
  });
};

/**
 * GET /api/uploads — paginated list of the user's live uploads.
 *
 * @param {{ cursor?: string|null, limit?: number, kind?: 'image'|'svg' }} opts
 * @returns {Promise<{ items: Array, next_cursor: string|null, has_more: boolean }>}
 */
export async function listUploads({ cursor, limit, kind } = {}) {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  if (kind) params.set('kind', kind);
  const qs = params.toString();
  const url = `${API_URL}/api/uploads${qs ? `?${qs}` : ''}`;

  let resp;
  try {
    resp = await fetch(url, { headers: _authHeader() });
  } catch (e) {
    throw new UploadsApiError('Network error while loading uploads.', { code: 'network' });
  }
  return _handle(resp);
}

/**
 * POST /api/uploads — multipart upload. Server is the only authority for
 * mime / size / quota; we let it speak.
 *
 * @param {File} file
 * @param {(loaded:number, total:number) => void} [onProgress] — optional
 *        XHR-based progress callback. Not all browsers expose progress on
 *        fetch; we fall back to a plain fetch when no callback is given.
 * @returns {Promise<object>} the upload descriptor
 */
export async function uploadFile(file, onProgress) {
  const fd = new FormData();
  fd.append('file', file);

  if (typeof onProgress === 'function') {
    // Use XHR so we can report progress; fetch() can't.
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/uploads`);
      const token = _token();
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) onProgress(ev.loaded, ev.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new UploadsApiError('Bad response.', { status: xhr.status })); }
          return;
        }
        let detail = `Upload failed (${xhr.status})`;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body && typeof body.detail === 'string') detail = body.detail;
        } catch { /* ignore */ }
        reject(new UploadsApiError(detail, { status: xhr.status, code: _statusToCode(xhr.status) }));
      };
      xhr.onerror = () => reject(new UploadsApiError('Network error during upload.', { code: 'network' }));
      xhr.send(fd);
    });
  }

  let resp;
  try {
    resp = await fetch(`${API_URL}/api/uploads`, {
      method: 'POST',
      headers: _authHeader(),
      body: fd,
    });
  } catch (e) {
    throw new UploadsApiError('Network error during upload.', { code: 'network' });
  }
  return _handle(resp);
}

/**
 * DELETE /api/uploads/:id — soft delete + quota refund.
 */
export async function deleteUpload(id) {
  let resp;
  try {
    resp = await fetch(`${API_URL}/api/uploads/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: _authHeader(),
    });
  } catch (e) {
    throw new UploadsApiError('Network error while deleting.', { code: 'network' });
  }
  return _handle(resp);
}

/**
 * GET /api/uploads/quota — { used_bytes, total_bytes, plan }.
 */
export async function getQuota() {
  let resp;
  try {
    resp = await fetch(`${API_URL}/api/uploads/quota`, { headers: _authHeader() });
  } catch (e) {
    throw new UploadsApiError('Network error while loading quota.', { code: 'network' });
  }
  return _handle(resp);
}

/**
 * Format bytes as "12 KB" / "1.4 MB" / "3.2 GB" for the quota bar / file rows.
 * Kept here (not in utils.js) because every consumer of these counts wants
 * the same formatting and we shouldn't ship two implementations.
 */
export function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export { UploadsApiError };
